/**
 * A primeira dobra da tela Saúde, em FRASES.
 *
 * POR QUE FRASES: "Tipo 4 de 6" não significa nada para um diretor, e a
 * primeira dobra é onde ele decide se o produto serve. O vocabulário técnico
 * (Fleuriet, DuPont, NCG) existe e é importante, mas mora dentro do bloco que
 * o explica, não na cara de quem abriu a tela pela primeira vez.
 *
 * Cada frase cita um número da própria empresa. Frase sem número é horóscopo.
 */

import { classificar, classificarTesouraria } from './faixas';
import { fraseDoCiclo } from './fleuriet';
import { formatarMoeda, formatarPercentual } from '../utils/series';
import { TELAS } from './catalogo';

/** A faixa do Índice de Saúde. Mesmos cortes que o painel já usava. */
export const faixaDoIndice = (nota) => {
  if (nota === null || nota === undefined) return { rotulo: 'Não calculado', cor: 'default' };
  if (nota >= 80) return { rotulo: 'Saudável', cor: 'success' };
  if (nota >= 60) return { rotulo: 'Estável', cor: 'success' };
  if (nota >= 40) return { rotulo: 'Requer atenção', cor: 'warning' };
  return { rotulo: 'Crítica', cor: 'error' };
};

/**
 * O selo que qualifica a nota sem escondê-la.
 *
 * Metade da base não tem balanço. Sumir com a nota entrega um vazio justamente
 * na tela que responde "minha empresa está saudável?"; dizer sobre o que ela
 * foi calculada entrega a nota E a ressalva.
 */
export const seloDoPerfil = (indiceSaude) => {
  if (!indiceSaude) return null;
  const { perfilNome, cobertura } = indiceSaude;
  const { pilaresConsiderados, pilaresPossiveis } = cobertura || {};
  const complemento = indiceSaude.perfil === 'perfil_dre'
    ? 'sem balanço patrimonial'
    : 'com balanço patrimonial';
  return `${perfilNome} — calculado sobre ${pilaresConsiderados} de ${pilaresPossiveis} pilares possíveis ${complemento}`;
};

/**
 * As frases da primeira dobra, na ordem em que o dono lê.
 *
 * Frase cujo insumo não existe simplesmente NÃO É GERADA. Nenhuma delas cai
 * para um texto genérico — "sua liquidez é 0,00" seria uma afirmação, e a
 * ausência de balanço não é uma medição de liquidez.
 *
 * @returns {Array<{id, texto, nivel, tela}>}
 */
export const frasesDaPrimeiraDobra = (leitura) => {
  if (!leitura) return [];
  const { valores, rotulos, temBalanco } = leitura;
  const mesRecente = rotulos?.[rotulos.length - 1] || 'o último mês';
  const frases = [];

  // 1. O resultado, com o mês nomeado. É a primeira pergunta de qualquer dono.
  if (valores.margemLiquidaCompetencia !== null) {
    const faixa = classificar('margemLiquida', valores.margemLiquidaCompetencia);
    frases.push({
      id: 'margem',
      texto: valores.margemLiquidaCompetencia >= 0
        ? `De cada R$ 100 vendidos em ${mesRecente}, sobraram ${formatarPercentual(valores.margemLiquidaCompetencia, 1)} depois de todos os custos.`
        : `Em ${mesRecente}, cada R$ 100 vendidos deixaram um prejuízo de ${formatarPercentual(Math.abs(valores.margemLiquidaCompetencia), 1)}.`,
      nivel: faixa.nivel,
      tela: TELAS.RESULTADO
    });
  }

  // 2. Margem de segurança: o quanto a venda pode cair antes de virar prejuízo.
  if (valores.margemSeguranca !== null) {
    const faixa = classificar('margemSeguranca', valores.margemSeguranca);
    frases.push({
      id: 'margemSeguranca',
      texto: valores.margemSeguranca >= 0
        ? `A venda pode cair ${formatarPercentual(valores.margemSeguranca, 0)} antes de a empresa passar a dar prejuízo.`
        : `A venda de ${mesRecente} ficou ${formatarPercentual(Math.abs(valores.margemSeguranca), 0)} abaixo do necessário para cobrir os custos.`,
      nivel: faixa.nivel,
      tela: TELAS.RESULTADO
    });
  }

  // 3. Caixa em reais. Runway só quando há queima real — com lucro positivo o
  //    indicador não se aplica, e exibi-lo como infinito seria ruído.
  if (valores.runwayMeses !== null) {
    const faixa = classificar('runwayMeses', valores.runwayMeses);
    frases.push({
      id: 'runway',
      texto: `No ritmo atual de queima, o caixa disponível cobre ${valores.runwayMeses.toFixed(1).replace('.', ',')} meses.`,
      nivel: faixa.nivel,
      tela: TELAS.CAIXA_CICLO
    });
  } else if (valores.saldoTesouraria !== null) {
    const faixa = classificarTesouraria(valores.saldoTesouraria, valores.receitaLiquida);
    frases.push({
      id: 'tesouraria',
      texto: valores.saldoTesouraria >= 0
        ? `Sobram ${formatarMoeda(valores.saldoTesouraria)} de folga de caixa depois de financiar o giro.`
        : `Faltam ${formatarMoeda(Math.abs(valores.saldoTesouraria))} para o giro se financiar sozinho — a diferença hoje vem de dívida de curto prazo.`,
      nivel: faixa.nivel,
      tela: TELAS.CAIXA_CICLO
    });
  }

  // 4. Posição do ciclo, dita como o dono fala.
  const ciclo = fraseDoCiclo(valores.ccc);
  if (ciclo) {
    frases.push({
      id: 'ciclo',
      texto: ciclo,
      nivel: classificar('cicloFinanceiro', valores.ccc).nivel,
      tela: TELAS.CAIXA_CICLO
    });
  }

  // 5. Sem balanço, a dobra diz o que está faltando em vez de fingir completude.
  if (!temBalanco) {
    frases.push({
      id: 'semBalanco',
      texto: 'A leitura de caixa, ciclo e endividamento ainda não existe: ela depende do balanço patrimonial.',
      nivel: 'ausente',
      tela: TELAS.DADOS
    });
  }

  return frases;
};

/** Os N sinais mais graves, já apontando para a tela que explica cada um. */
export const sinaisMaisGraves = (leitura, quantidade = 3) => {
  if (!leitura) return [];
  const criticos = (leitura.alertas?.criticos || []).map((a) => ({
    severidade: 'critical',
    mensagem: a.mensagem || a.message,
    recomendacao: a.recomendacao || null
  }));
  const atencao = (leitura.alertas?.atencao || []).map((a) => ({
    severidade: 'warning',
    mensagem: a.mensagem || a.message,
    recomendacao: a.recomendacao || null
  }));
  return [...criticos, ...atencao].filter((s) => s.mensagem).slice(0, quantidade);
};
